/**
 * Ficha de una palabra guardada desde la página independiente. Misma vista que
 * {@link SavedVocabDetailPage} (Progreso), pero vuelve a /saved-words.
 */

import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from '@/components/atoms/Spinner';
import { VocabularyFlashcard } from '@/components/molecules/VocabularyFlashcard';
import { ROUTES } from '@/constants/routes';
import { useVocabularyStudyTimer } from '@/hooks/useVocabularyStudyTimer';
import { useSavedVocabulary } from '@/context/SavedVocabularyContext';
import { savedVocabularyToVocabItem } from '@/pages/student/LearnedWordsPages/utils/savedVocabularyToVocabItem';
import type { VocabItem } from '@/types/vocabulary.types';
import styles from '@/pages/student/LearnedWordsPages/LearnedWordVocabLayout.module.scss';

export function SavedWordDetailPage() {
  const { t } = useTranslation();
  const { savedId } = useParams<{ savedId: string }>();
  const navigate = useNavigate();
  const { savedItems, removeItem, isSaved, isLoading } = useSavedVocabulary();

  const navigateToSavedList = useCallback(() => {
    navigate(ROUTES.STUDENT.SAVED_WORDS);
  }, [navigate]);

  const savedRow = useMemo(() => {
    if (!savedId) return null;
    return savedItems.find((s) => s.id === savedId) ?? null;
  }, [savedId, savedItems]);

  const vocabItem: VocabItem | null = useMemo(
    () => (savedRow ? savedVocabularyToVocabItem(savedRow) : null),
    [savedRow]
  );

  useVocabularyStudyTimer(Boolean(vocabItem) && !isLoading);

  const handleToggleSaveVocab = useCallback(
    async (v: VocabItem) => {
      if (isSaved(v.word)) {
        await removeItem(v.word);
        navigateToSavedList();
      }
    },
    [isSaved, removeItem, navigateToSavedList]
  );

  if (isLoading) {
    return (
      <div className={styles.vocabPage}>
        <div className={styles.vocabPage__state}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </div>
      </div>
    );
  }

  if (!vocabItem || !savedRow) {
    return (
      <div className={styles.vocabPage}>
        <div className={styles.vocabPage__state}>
          <p>{t('learnedWordsList.wordNotFound')}</p>
          <button
            type="button"
            data-no-press-scale=""
            className={styles.vocabPage__retry}
            onClick={navigateToSavedList}
          >
            {t('learnedWordsList.backToList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.vocabPage}>
      <div className={styles.vocabPage__card}>
        <VocabularyFlashcard
          className={styles.vocabPage__vocabStretch}
          item={vocabItem}
          isSaved
          onToggleSave={handleToggleSaveVocab}
        />
      </div>
    </div>
  );
}

SavedWordDetailPage.displayName = 'SavedWordDetailPage';
export default SavedWordDetailPage;
